import React, { useEffect, useState } from "react";
import { Calculator, AlertTriangle, CheckCircle2, FileText, Loader2, Info, Download } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import jsPDF from "jspdf";
import { toast } from "sonner";

const fmt = (n) => (n || 0).toLocaleString("sv-SE").replace(/,/g, " ") + " kr";

export default function SanctionCalculator() {
  const { user } = useAuth();
  const [types, setTypes] = useState([]);
  const [actionType, setActionType] = useState("");
  const [area, setArea] = useState("");
  const [isOneOrTwoDwelling, setIsOneOrTwoDwelling] = useState(true);
  const [rectified, setRectified] = useState(false);
  const [voluntary, setVoluntary] = useState(false);
  const [hardship, setHardship] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/sanction/action-types").then((r) => setTypes(r.data));
  }, []);

  const selectedType = types.find((t) => t.id === actionType);

  const calculate = async (e) => {
    e?.preventDefault?.();
    if (!actionType) return toast.error("Välj en åtgärdstyp");
    setBusy(true);
    try {
      const { data } = await api.post("/sanction/calculate", {
        action_type: actionType,
        area_sqm: parseFloat(area) || 0,
        is_one_or_two_dwelling: isOneOrTwoDwelling,
        rectified,
        voluntary_disclosure: voluntary,
        economic_hardship: hardship,
      });
      setResult(data);
      window.scrollTo({ top: 400, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Beräkning misslyckades");
    } finally {
      setBusy(false);
    }
  };

  const exportPdf = () => {
    if (!result) return;
    const doc = new jsPDF();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Beslut om byggsanktionsavgift", 14, 20);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(result.decision_text, 180);
    doc.text(lines, 14, 32);
    doc.save(`sanktion-${result.action_type}-${result.total}kr.pdf`);
  };

  const reset = () => {
    setActionType(""); setArea(""); setRectified(false);
    setVoluntary(false); setHardship(false); setResult(null);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-in-up" data-testid="sanction-page">
      <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Sanktion / Tillsyn</div>
      <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>
        Byggsanktionsavgift
      </h1>
      <p className="mt-2 text-sm text-zinc-600 max-w-2xl">
        Beräknar avgift enligt PBL 11 kap 51-53a § och PBF 9 kap baserat på prisbasbeloppet 2026 (58 800 kr).
        Resultatet är ett underlag – det slutgiltiga beslutet fattas av byggnadsnämnden.
      </p>

      <form onSubmit={calculate} className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Vänster: input */}
        <div className="bg-white border border-zinc-200 rounded-sm p-6 space-y-5">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
              Åtgärdstyp <span className="text-red-600">*</span>
            </label>
            <select value={actionType} onChange={(e) => { setActionType(e.target.value); setResult(null); }}
              data-testid="action-type-select"
              className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm bg-white outline-none focus:border-blue-700 text-sm">
              <option value="">— välj åtgärd —</option>
              {types.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
            {selectedType && (
              <p className="mt-1 text-[11px] text-zinc-500">
                Lagrum: <span className="font-medium">{selectedType.law_ref}</span>
              </p>
            )}
          </div>

          {selectedType?.requires_area && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                Area (m²)
              </label>
              <input type="number" step="0.1" min="0" value={area}
                onChange={(e) => setArea(e.target.value)} data-testid="area-input"
                placeholder={`Grundyta: ${selectedType.grund_yta_m2} m²`}
                className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm outline-none focus:border-blue-700 text-sm" />
              {selectedType.grund_yta_m2 > 0 && (
                <p className="mt-1 text-[11px] text-zinc-500">
                  Tillägg räknas från och med {selectedType.grund_yta_m2} m².
                </p>
              )}
            </div>
          )}

          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">Fastighetstyp</div>
            <div className="flex gap-2">
              <button type="button"
                onClick={() => setIsOneOrTwoDwelling(true)}
                data-testid="dwelling-yes"
                className={`flex-1 px-3 py-2 text-sm border rounded-sm transition ${
                  isOneOrTwoDwelling ? "bg-blue-700 border-blue-700 text-white" : "bg-white border-zinc-300 hover:bg-zinc-50"
                }`}>En-/tvåbostadshus</button>
              <button type="button"
                onClick={() => setIsOneOrTwoDwelling(false)}
                data-testid="dwelling-no"
                className={`flex-1 px-3 py-2 text-sm border rounded-sm transition ${
                  !isOneOrTwoDwelling ? "bg-blue-700 border-blue-700 text-white" : "bg-white border-zinc-300 hover:bg-zinc-50"
                }`}>Övrig byggnad</button>
            </div>
          </div>

          <div className="space-y-2 pt-2 border-t border-zinc-100">
            <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1">
              Nedsättningsgrunder (PBL 11 kap 53a §)
            </div>
            <label className="flex items-start gap-2 cursor-pointer text-sm text-zinc-700 hover:bg-zinc-50 p-1.5 rounded-sm">
              <input type="checkbox" checked={rectified} onChange={(e) => setRectified(e.target.checked)}
                data-testid="rectified-checkbox" className="mt-0.5 accent-blue-700" />
              <span>Åtgärden har rättats – × 0.5</span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer text-sm text-zinc-700 hover:bg-zinc-50 p-1.5 rounded-sm">
              <input type="checkbox" checked={voluntary} onChange={(e) => setVoluntary(e.target.checked)}
                data-testid="voluntary-checkbox" className="mt-0.5 accent-blue-700" />
              <span>Frivillig anmälan av överträdelsen – × 0.75</span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer text-sm text-zinc-700 hover:bg-zinc-50 p-1.5 rounded-sm">
              <input type="checkbox" checked={hardship} onChange={(e) => setHardship(e.target.checked)}
                data-testid="hardship-checkbox" className="mt-0.5 accent-blue-700" />
              <span>Oskäligt belopp ift. överträdelsens art – × 0.5</span>
            </label>
          </div>

          <div className="pt-4 flex gap-2">
            <button type="submit" disabled={busy || !actionType} data-testid="calculate-btn"
              className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-700 hover:bg-blue-800 disabled:opacity-50 text-white text-sm font-medium rounded-sm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
              Beräkna avgift
            </button>
            {result && (
              <button type="button" onClick={reset}
                className="px-4 py-2.5 border border-zinc-300 rounded-sm text-sm hover:bg-zinc-50">
                Återställ
              </button>
            )}
          </div>
        </div>

        {/* Höger: resultat */}
        <div data-testid="sanction-result">
          {!result && (
            <div className="bg-zinc-50 border border-dashed border-zinc-300 rounded-sm p-10 text-center h-full flex flex-col justify-center">
              <Calculator className="w-10 h-10 text-zinc-300 mx-auto stroke-[1.5]" />
              <p className="mt-3 text-sm text-zinc-500">Fyll i åtgärd och tryck &quot;Beräkna avgift&quot;.</p>
            </div>
          )}

          {result && (
            <div className={`border rounded-sm p-6 ${
              result.verdict === "avgift_ska_tas_ut" ? "bg-red-50 border-red-300" : "bg-emerald-50 border-emerald-300"
            }`}>
              <div className="flex items-start gap-3">
                {result.verdict === "avgift_ska_tas_ut"
                  ? <AlertTriangle className="w-5 h-5 text-red-700 mt-0.5" />
                  : <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5" />}
                <div className="flex-1">
                  <div className={`text-xs uppercase tracking-wider font-semibold ${
                    result.verdict === "avgift_ska_tas_ut" ? "text-red-700" : "text-emerald-700"
                  }`}>{result.verdict_label}</div>
                  <div className="text-4xl font-semibold mt-1" style={{ fontFamily: "Outfit" }} data-testid="sanction-total">
                    {fmt(result.total)}
                  </div>
                  <div className="mt-1 text-xs text-zinc-600">{result.action_label}</div>
                </div>
              </div>

              <table className="mt-5 w-full text-sm">
                <tbody className="divide-y divide-zinc-200">
                  <tr><td className="py-2 text-zinc-600">Grundbelopp</td><td className="py-2 text-right font-medium">{fmt(result.grund_belopp)}</td></tr>
                  <tr><td className="py-2 text-zinc-600">Tilläggsbelopp</td><td className="py-2 text-right font-medium">{fmt(result.tillagg_belopp)}</td></tr>
                  <tr><td className="py-2 text-zinc-600">Delsumma</td><td className="py-2 text-right font-medium">{fmt(result.subtotal)}</td></tr>
                  {result.reductions?.map((r, i) => (
                    <tr key={i}><td className="py-2 text-amber-700 text-xs">↓ {r.reason}</td><td className="py-2 text-right text-amber-700">× {r.factor}</td></tr>
                  ))}
                  <tr className="bg-zinc-100"><td className="py-2 px-2 font-semibold">Total avgift</td><td className="py-2 px-2 text-right text-lg font-semibold">{fmt(result.total)}</td></tr>
                </tbody>
              </table>

              <div className="mt-5">
                <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">Beräkningssteg</div>
                <ul className="space-y-1 text-xs text-zinc-700">
                  {result.reasoning?.map((r, i) => <li key={i}>• {r}</li>)}
                </ul>
              </div>

              <div className="mt-4 pt-4 border-t border-zinc-200">
                <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">Lagrum</div>
                <div className="flex flex-wrap gap-1.5">
                  {result.law_refs?.map((l, i) => (
                    <span key={i} className="px-2 py-0.5 bg-white border border-zinc-300 text-xs rounded-sm">{l}</span>
                  ))}
                </div>
              </div>

              {result.warnings?.length > 0 && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-sm">
                  <div className="text-xs font-semibold text-amber-900 mb-1 flex items-center gap-1">
                    <Info className="w-3.5 h-3.5" /> Att tänka på
                  </div>
                  <ul className="text-xs text-amber-900 space-y-0.5">
                    {result.warnings.map((w, i) => <li key={i}>• {w}</li>)}
                  </ul>
                </div>
              )}

              <div className="mt-5 flex gap-2">
                <button onClick={exportPdf} data-testid="sanction-pdf-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-900 hover:bg-zinc-800 text-white text-sm rounded-sm">
                  <Download className="w-4 h-4" /> Hämta beslut (PDF)
                </button>
              </div>
            </div>
          )}
        </div>
      </form>

      {/* Decision text preview */}
      {result?.decision_text && (
        <div className="mt-6 bg-white border border-zinc-200 rounded-sm p-6" data-testid="decision-text">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-4 h-4 text-zinc-500" />
            <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Beslutstext</div>
          </div>
          <pre className="text-xs text-zinc-700 whitespace-pre-wrap font-mono leading-relaxed">{result.decision_text}</pre>
        </div>
      )}
    </div>
  );
}
